import { AugmentationToolbarOption } from "@pathwright/ux/src/components/augmentation/AugmentationToolbar"
import { match } from "react-router-dom"
import {
  useManageMentorGroupMembershipsQuery,
  useMentorGroupQuery,
  useUserPathReportCardQuery,
  useUserProfileQuery
} from "../../api/generated"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import ProfileAugmentation from "../../profile/augmentation/ProfileAugmentation"
import ProfileRegistrationConfigureAugmentation from "../../profile/augmentation/ProfileRegistrationConfigureAugmentation"
import ProfileRegistrationReportCardAugmentation from "../../profile/augmentation/ProfileRegistrationReportCardAugmentation"
import { useCanViewSimpleNav } from "../../unified-home/useCanViewUnifiedHome"
import { canViewMentorGroups } from "../../user/permissions"
import GroupAddAugmentation from "./GroupAddAugmentation"
import GroupAugmentation from "./GroupAugmentation"
import GroupManageAugmentation from "./GroupManageAugmentation"
import GroupMemberMembershipAugmentation from "./GroupMemberMembershipAugmentation"
import GroupMembersAugmentation from "./GroupMembersAugmentation"

function useGroupAugmentationToolbarOption(): AugmentationToolbarOption | null {
  const pwContext = usePathwrightContext()
  const userCanViewMentorGroups = canViewMentorGroups(pwContext)
  const userCanViewSimpleNav = useCanViewSimpleNav()
  const query = useManageMentorGroupMembershipsQuery({
    skip: !userCanViewMentorGroups
  })

  // Check if the user has only 1, admin membership and if so,
  // link directly to the related mentor group.
  const total = query.data?.me?.mentorGroupMemberships?.total
  const adminMembershipEdge =
    query.data?.me?.mentorGroupMemberships?.edges?.find(
      (e) => e?.node?.role === 15
    )
  const groupId =
    total === 1 && adminMembershipEdge
      ? adminMembershipEdge.node?.group_id
      : null

  const pathname = groupId
    ? `/community/groups/${groupId}/`
    : "/community/groups/"

  if (userCanViewMentorGroups) {
    function getGroupAugmentationForDepth(depth: number = 5) {
      // Use an inner function to recurse upwards to depth (otherwise we could avoid an inner
      // function if recursing downwards to 0).
      function inner(currentDepth: number = 1) {
        const groupIdVar = `groupId${currentDepth}`

        function getGroupId(match: match<{ [key: string]: string }>) {
          const groupId = match.params[groupIdVar]
          if (!groupId) return null
          const parsedGroupId = parseInt(groupId)
          if (isNaN(parsedGroupId)) return null
          return parsedGroupId
        }

        const augmentations: Omit<AugmentationToolbarOption, "icon"> = {
          key: `:${groupIdVar}`,
          AugmentationLabelComponent: (props) => {
            const groupId = getGroupId(props.match)
            const groupQuery = useMentorGroupQuery({
              variables: { id: groupId! }
            })
            const groupName =
              groupQuery.data?.school?.mentor_group?.name || null
            return <>{groupName}</>
          },
          label: "Group",
          AugmentationComponent: (props) => (
            <GroupMembersAugmentation groupId={getGroupId(props.match)!} />
          ),
          children: [
            {
              key: "add-group",
              label: "Add Group",
              AugmentationComponent: (props) => (
                <GroupAddAugmentation groupId={getGroupId(props.match)!} />
              )
            },
            {
              key: "manage",
              label: "Manage",
              AugmentationComponent: (props) => (
                <GroupManageAugmentation groupId={getGroupId(props.match)!} />
              )
            },
            {
              key: ":manageMember(manage-member-\\d+)",
              label: "Configure Membership",
              AugmentationComponent: (props) => (
                <GroupMemberMembershipAugmentation
                  groupId={getGroupId(props.match)!}
                />
              )
            },
            {
              key: "member/:userId",
              label: "Group Member",
              AugmentationLabelComponent: (props) => {
                const userId = parseInt(props.match.params.userId)
                const userProfileQuery = useUserProfileQuery({
                  variables: {
                    id: userId
                  }
                })
                const userName =
                  userProfileQuery.data?.user?.display_name || null
                return <>{userName}</>
              },
              AugmentationComponent: ProfileAugmentation,
              children: [
                {
                  key: "registration/:registrationId",
                  label: "Registration Settings",
                  AugmentationComponent:
                    ProfileRegistrationConfigureAugmentation
                },
                {
                  key: ":pathId",
                  label: "Path",
                  AugmentationLabelComponent: (props) => {
                    const pathId = parseInt(props.match.params.pathId)!
                    const userPathReportCardQuery = useUserPathReportCardQuery({
                      variables: {
                        id: pathId
                      }
                    })

                    const pathName =
                      userPathReportCardQuery.data?.path?.cohort?.name || null

                    return <>{pathName}</>
                  },
                  AugmentationComponent:
                    ProfileRegistrationReportCardAugmentation
                }
              ]
            }
          ]
        }

        if (currentDepth < depth) {
          // NOTE: group route must be last
          augmentations.children?.push(inner(currentDepth + 1))
        }

        return augmentations
      }

      return inner(1)
    }

    return userCanViewSimpleNav
      ? {
          key: "groups",
          icon: "avatar-stack-fill",
          label: "Your Groups",
          AugmentationComponent: GroupAugmentation,
          children: [
            {
              key: "add-group",
              label: "Add Group",
              AugmentationComponent: ({ match }) => (
                <GroupAddAugmentation
                  groupId={parseInt(
                    (match as match<{ groupId: string }>).params.groupId
                  )}
                />
              )
            },
            getGroupAugmentationForDepth(5)
          ]
        }
      : {
          key: "groups",
          icon: "avatar-stack-fill",
          label: "Groups",
          to: pathname,
          // HACK: (well sort of), we don't want to return to the home
          // augmentation panel (if it was open previously) when closing
          // the mentor groups augmentation.
          onClick: () => {
            setTimeout(() => {
              // @ts-expect-error
              window.App.getStore("navigation")._mutate({
                modal_launched_from_url: "/home"
              })
            }, 100)
          }
        }
  }

  return null
}

export default useGroupAugmentationToolbarOption
